import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Link, useLocation, useNavigate } from 'react-router';
import { useUI } from '../../context/UIContext';
import { useUser } from '../../context/UserContext';
import { hasAnyAdminAccess } from '../../config/adminNav';
import { useTheme } from '../../context/ThemeContext';
import { useConversation } from '../../context/ConversationContext';
import { useFolder } from '../../context/FolderContext';
import { useProject } from '../../context/ProjectContext';
import { ConversationListSkeleton, ConfirmModal, Toast, SettingsModal, ProjectSettingsModal, MoveToProjectModal, PromptLibraryModal, OrgPicker, LocationSwitcher } from '../ui';
import BuildInfo from '../ui/BuildInfo';
import ResizeHandle from './ResizeHandle';
import HorizontalResizeHandle from './HorizontalResizeHandle';
import FolderItem from './FolderItem';
import ProjectSelector from './ProjectSelector';
import SearchFilters from './SearchFilters';
import { csrfFetch } from '../../utils/csrfFetch';

/**
 * HighlightText - Highlights matching text in search results
 */
function HighlightText({ text, query }) {
  if (!query || !text) return <>{text}</>;

  const lowerText = text.toLowerCase();
  const lowerQuery = query.toLowerCase();
  const parts = [];
  let lastIndex = 0;
  let index = lowerText.indexOf(lowerQuery);

  while (index !== -1) {
    // Add text before match
    if (index > lastIndex) {
      parts.push({ text: text.substring(lastIndex, index), highlight: false });
    }
    // Add highlighted match
    parts.push({ text: text.substring(index, index + query.length), highlight: true });
    lastIndex = index + query.length;
    index = lowerText.indexOf(lowerQuery, lastIndex);
  }

  // Add remaining text
  if (lastIndex < text.length) {
    parts.push({ text: text.substring(lastIndex), highlight: false });
  }

  return (
    <>
      {parts.map((part, i) =>
        part.highlight ? (
          <mark key={i} className="bg-yellow-300 dark:bg-yellow-600 text-gray-900 dark:text-white rounded-sm px-0.5">
            {part.text}
          </mark>
        ) : (
          <span key={i}>{part.text}</span>
        )
      )}
    </>
  );
}

/**
 * UnreadBadge - Shows unread indicator for conversations (SAT-100)
 * Shows a blue dot for single unread, or a count badge for multiple
 */
function UnreadBadge({ count }) {
  if (!count || count === 0) return null;

  if (count === 1) {
    // Single unread - show blue dot
    return (
      <span
        className="w-2 h-2 bg-blue-500 rounded-full shrink-0 animate-pulse"
        title="1 unread message"
      />
    );
  }

  // Multiple unread - show count badge
  return (
    <span
      className="min-w-5 h-5 px-1.5 bg-blue-500 text-white text-xs font-bold
        rounded-full flex items-center justify-center shrink-0"
      title={`${count} unread messages`}
    >
      {count > 99 ? '99+' : count}
    </span>
  );
}

/**
 * Sidebar - Left navigation panel with conversation list
 *
 * Features:
 * - New chat button
 * - Search conversations
 * - Conversation list (grouped by date)
 * - Settings and user profile at bottom
 * - Collapsible with smooth transition
 * - Resizable width with drag handle
 */
function Sidebar() {
  const location = useLocation();
  const navigate = useNavigate();
  const {
    sidebarOpen, sidebarWidth, setSidebarWidth, toggleSidebar,
    adminPanelExpanded, adminPanelHeight, setAdminPanelHeight, toggleAdminPanel,
  } = useUI();
  const userCtx = useUser();
  const { user, org, role, isAuthenticated, logout } = userCtx;
  const canSeeAdmin = hasAnyAdminAccess(userCtx);
  const { isDark, toggleTheme } = useTheme();
  const { conversations: contextConversations, refreshCounter, fetchConversations, getUnreadCount, totalUnread } = useConversation();
  const {
    folders,
    isLoading: foldersLoading,
    createFolder,
    addConversationToFolder,
    getFolderedConversationIds
  } = useFolder();
  const [isLoading, setIsLoading] = useState(true);
  const [apiError, setApiError] = useState(false);

  // Folder state
  const [showNewFolderInput, setShowNewFolderInput] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const newFolderInputRef = useRef(null);

  const [grouped, setGrouped] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [isSearching, setIsSearching] = useState(false);
  const searchTimeoutRef = useRef(null);

  // Advanced search filters state (SAT-109)
  const [isFiltersExpanded, setIsFiltersExpanded] = useState(false);
  const [advancedFilters, setAdvancedFilters] = useState({
    selectedProjects: [],
    dateFrom: '',
    dateTo: '',
    selectedModels: [],
  });

  // Collapsible groups state
  const [collapsedGroups, setCollapsedGroups] = useState({});

  // Archive filter state - when true, show only archived conversations
  const [showArchived, setShowArchived] = useState(false);

  // Project filter state from context (SAT-63, SAT-64)
  const { selectedProject, setSelectedProject, updateProject } = useProject();

  // Context menu state
  const [contextMenu, setContextMenu] = useState({ visible: false, x: 0, y: 0, conv: null });
  const [editingConvId, setEditingConvId] = useState(null);
  const [editingTitle, setEditingTitle] = useState('');
  const editInputRef = useRef(null);

  // Delete confirmation state
  const [deleteModal, setDeleteModal] = useState({ visible: false, conv: null });
  const [isDeleting, setIsDeleting] = useState(false);

  // Settings modal state (SAT-72)
  const [showSettingsModal, setShowSettingsModal] = useState(false);

  // Project settings modal state (SAT-64)
  const [projectSettingsModal, setProjectSettingsModal] = useState({ visible: false, project: null });

  // Move to project modal state (SAT-65)
  const [moveModal, setMoveModal] = useState({ visible: false, conv: null });

  // Prompt library modal state (SAT-79)
  const [showPromptLibrary, setShowPromptLibrary] = useState(false);

  // Undo toast state
  const [undoToast, setUndoToast] = useState({ visible: false, conv: null, timeoutId: null });
  const deletedConvRef = useRef(null); // Store deleted conv data for undo

  // Fetch conversations from API with auto-retry on transient failures
  const retryCount = useRef(0);
  const loadConversations = useCallback(async () => {
    try {
      const response = await csrfFetch('/api/conversations', {
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setGrouped(data.conversations || {});
        setApiError(false);
        retryCount.current = 0;
      } else if (response.status === 401) {
        // Session expired — the global 401 interceptor in UserContext handles redirect
        setGrouped({});
        setApiError(false); // don't show error, redirect will happen
      } else {
        console.error('Error fetching conversations:', response.status);
        setGrouped({});
        setApiError(true);
      }
    } catch (error) {
      console.error('Error fetching conversations:', error);
      // Auto-retry up to 3 times with backoff for network errors
      if (retryCount.current < 3) {
        retryCount.current++;
        const delay = retryCount.current * 2000; // 2s, 4s, 6s
        setTimeout(() => loadConversations(), delay);
        return; // don't set error state yet
      }
      setGrouped({});
      setApiError(true);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Load on mount
  useEffect(() => {
    loadConversations();
  }, [loadConversations]);

  // Refresh when context signals a refresh (fetchConversations was called)
  useEffect(() => {
    if (refreshCounter > 0) {
      // Reload from API when context signals a refresh
      loadConversations();
    }
  }, [refreshCounter, loadConversations]);

  // Update from context conversations if available
  useEffect(() => {
    if (contextConversations && typeof contextConversations === 'object' && Object.keys(contextConversations).length > 0) {
      setGrouped(contextConversations);
      setIsLoading(false);
    }
  }, [contextConversations]);


  // Toggle group collapse/expand
  const toggleGroupCollapse = useCallback((date) => {
    setCollapsedGroups(prev => ({
      ...prev,
      [date]: !prev[date]
    }));
  }, []);

  // Close context menu when clicking outside
  useEffect(() => {
    const handleClick = () => setContextMenu({ visible: false, x: 0, y: 0, conv: null });
    if (contextMenu.visible) {
      document.addEventListener('click', handleClick);
      return () => document.removeEventListener('click', handleClick);
    }
  }, [contextMenu.visible]);

  // Focus edit input when editing
  useEffect(() => {
    if (editingConvId && editInputRef.current) {
      editInputRef.current.focus();
      editInputRef.current.select();
    }
  }, [editingConvId]);

  // Handle right-click on conversation
  const handleContextMenu = (e, conv) => {
    e.preventDefault();
    e.stopPropagation();
    setContextMenu({
      visible: true,
      x: e.clientX,
      y: e.clientY,
      conv
    });
  };

  // Start inline editing
  const handleStartRename = (conv) => {
    setEditingConvId(conv.id);
    setEditingTitle(conv.title || '');
    setContextMenu({ visible: false, x: 0, y: 0, conv: null });
  };

  // Save renamed title
  const handleSaveRename = async () => {
    if (!editingConvId || !editingTitle.trim()) {
      setEditingConvId(null);
      return;
    }

    try {
      const response = await csrfFetch(`/api/conversations/${editingConvId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ title: editingTitle.trim() })
      });

      if (response.ok) {
        // Update local state
        setGrouped(prev => {
          const updated = { ...prev };
          for (const [date, convs] of Object.entries(updated)) {
            updated[date] = convs.map(c =>
              c.id === editingConvId ? { ...c, title: editingTitle.trim() } : c
            );
          }
          return updated;
        });
        // Also refresh from server to ensure sync
        fetchConversations();
      }
    } catch (error) {
      console.error('Error renaming conversation:', error);
    }

    setEditingConvId(null);
  };

  // Handle edit input key press
  const handleEditKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSaveRename();
    } else if (e.key === 'Escape') {
      setEditingConvId(null);
    }
  };

  // Show delete confirmation
  const handleShowDeleteModal = (conv) => {
    setDeleteModal({ visible: true, conv });
    setContextMenu({ visible: false, x: 0, y: 0, conv: null });
  };

  // Handle duplicate conversation
  const handleDuplicate = async (conv) => {
    setContextMenu({ visible: false, x: 0, y: 0, conv: null });

    try {
      const response = await csrfFetch(`/api/conversations/${conv.id}/duplicate`, {
        method: 'POST',
        credentials: 'include'
      });

      if (response.ok) {
        const newConversation = await response.json();
        // Refresh the conversation list
        if (refreshConversations) {
          await refreshConversations();
        }
        // Navigate to the new conversation
        navigate(`/chat/${newConversation.id}`);
      } else {
        const error = await response.json();
        console.error('Failed to duplicate conversation:', error);
      }
    } catch (err) {
      console.error('Error duplicating conversation:', err);
    }
  };

  // Handle showing move to project modal (SAT-65)
  const handleShowMoveModal = (conv) => {
    setContextMenu({ visible: false, x: 0, y: 0, conv: null });
    setMoveModal({ visible: true, conv });
  };

  // Handle moving conversation to different project (SAT-65)
  const handleMoveToProject = async (conversationId, projectId) => {
    try {
      const response = await csrfFetch(`/api/conversations/${conversationId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ projectId })
      });

      if (response.ok) {
        // Refresh the conversation list
        if (refreshConversations) {
          await refreshConversations();
        }
        setMoveModal({ visible: false, conv: null });
      } else {
        const error = await response.json();
        console.error('Failed to move conversation:', error);
      }
    } catch (err) {
      console.error('Error moving conversation:', err);
    }
  };

  // Close delete modal
  const handleCloseDeleteModal = () => {
    setDeleteModal({ visible: false, conv: null });
  };

  // Perform delete with undo option
  const handleConfirmDelete = async () => {
    const conv = deleteModal.conv;
    if (!conv) return;

    setIsDeleting(true);

    try {
      const response = await csrfFetch(`/api/conversations/${conv.id}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (response.ok) {
        // Store deleted conversation data for potential undo
        deletedConvRef.current = { ...conv };

        // Remove from local state immediately
        setGrouped(prev => {
          const updated = { ...prev };
          for (const [date, convs] of Object.entries(updated)) {
            updated[date] = convs.filter(c => c.id !== conv.id);
            // Remove empty date groups
            if (updated[date].length === 0) {
              delete updated[date];
            }
          }
          return updated;
        });

        // Close modal
        setDeleteModal({ visible: false, conv: null });

        // If currently viewing this conversation, navigate away
        if (location.pathname === `/chat/${conv.id}`) {
          navigate('/');
        }

        // Show undo toast
        const timeoutId = setTimeout(() => {
          setUndoToast({ visible: false, conv: null, timeoutId: null });
          deletedConvRef.current = null;
        }, 5000);

        setUndoToast({ visible: true, conv, timeoutId });
      } else {
        console.error('Failed to delete conversation');
      }
    } catch (error) {
      console.error('Error deleting conversation:', error);
    } finally {
      setIsDeleting(false);
    }
  };

  // Handle undo delete
  const handleUndoDelete = async () => {
    const conv = deletedConvRef.current;
    if (!conv) return;

    // Clear the timeout
    if (undoToast.timeoutId) {
      clearTimeout(undoToast.timeoutId);
    }

    try {
      // Restore conversation via API (undelete)
      const response = await csrfFetch(`/api/conversations/${conv.id}/restore`, {
        method: 'POST',
        credentials: 'include'
      });

      if (response.ok) {
        // Refresh conversation list
        fetchConversations();
      } else {
        // If no restore endpoint, just refresh to get current state
        fetchConversations();
      }
    } catch (error) {
      console.error('Error restoring conversation:', error);
      // Still try to refresh
      fetchConversations();
    }

    setUndoToast({ visible: false, conv: null, timeoutId: null });
    deletedConvRef.current = null;
  };

  // Close undo toast
  const handleCloseUndoToast = () => {
    if (undoToast.timeoutId) {
      clearTimeout(undoToast.timeoutId);
    }
    setUndoToast({ visible: false, conv: null, timeoutId: null });
  };

  // Focus new folder input when shown
  useEffect(() => {
    if (showNewFolderInput && newFolderInputRef.current) {
      newFolderInputRef.current.focus();
    }
  }, [showNewFolderInput]);

  // Handle create new folder
  const handleCreateFolder = async () => {
    if (!newFolderName.trim()) {
      setShowNewFolderInput(false);
      setNewFolderName('');
      return;
    }

    try {
      await createFolder(newFolderName.trim());
      setNewFolderName('');
      setShowNewFolderInput(false);
    } catch (err) {
      console.error('Failed to create folder:', err);
    }
  };

  // Handle folder name input key press
  const handleFolderNameKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleCreateFolder();
    } else if (e.key === 'Escape') {
      setShowNewFolderInput(false);
      setNewFolderName('');
    }
  };

  // Handle drag over for folder drop
  const handleFolderDragOver = (e, folderId) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  // Handle drop on folder
  const handleFolderDrop = async (e, folderId) => {
    e.preventDefault();
    try {
      const data = JSON.parse(e.dataTransfer.getData('application/json'));
      if (data.type === 'conversation' && data.conversationId) {
        await addConversationToFolder(folderId, data.conversationId);
      }
    } catch (err) {
      console.error('Drop error:', err);
    }
  };

  // Handle conversation drag start
  const handleConversationDragStart = (e, conv) => {
    e.dataTransfer.setData('application/json', JSON.stringify({
      type: 'conversation',
      conversationId: conv.id
    }));
    e.dataTransfer.effectAllowed = 'move';
  };

  // Debounced API search
  const performSearch = useCallback(async (query) => {
    if (!query || query.trim().length === 0) {
      setSearchResults(null);
      setIsSearching(false);
      return;
    }

    setIsSearching(true);
    try {
      const response = await csrfFetch(`/api/conversations/search?q=${encodeURIComponent(query)}`, {
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setSearchResults(data.conversations || {});
      } else {
        // Fallback to local filter
        setSearchResults(null);
      }
    } catch (error) {
      console.error('Search error:', error);
      setSearchResults(null);
    } finally {
      setIsSearching(false);
    }
  }, []);

  // Handle search input change with debouncing
  const handleSearchChange = useCallback((e) => {
    const query = e.target.value;
    setSearchQuery(query);

    // Clear existing timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    // Debounce API call
    if (query.trim().length > 0) {
      setIsSearching(true);
      searchTimeoutRef.current = setTimeout(() => {
        performSearch(query);
      }, 300);
    } else {
      setSearchResults(null);
      setIsSearching(false);
    }
  }, [performSearch]);

  // Clear search
  const handleClearSearch = useCallback(() => {
    setSearchQuery('');
    setSearchResults(null);
    setIsSearching(false);
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
  }, []);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  // Handle pin/unpin conversation
  const handleTogglePin = async (conv) => {
    const newPinned = !conv.isPinned;
    setContextMenu({ visible: false, x: 0, y: 0, conv: null });

    try {
      const response = await csrfFetch(`/api/conversations/${conv.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ isPinned: newPinned })
      });

      if (response.ok) {
        // Update local state
        setGrouped(prev => {
          const updated = { ...prev };
          for (const [date, convs] of Object.entries(updated)) {
            updated[date] = convs.map(c =>
              c.id === conv.id ? { ...c, isPinned: newPinned } : c
            );
          }
          return updated;
        });
        // Refresh from server
        fetchConversations();
      }
    } catch (error) {
      console.error('Error toggling pin:', error);
    }
  };

  // Handle archive/unarchive conversation
  const handleToggleArchive = async (conv) => {
    const newArchived = !conv.isArchived;
    setContextMenu({ visible: false, x: 0, y: 0, conv: null });

    try {
      const response = await csrfFetch(`/api/conversations/${conv.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ isArchived: newArchived })
      });

      if (response.ok) {
        // Update local state
        setGrouped(prev => {
          const updated = { ...prev };
          for (const [date, convs] of Object.entries(updated)) {
            updated[date] = convs.map(c =>
              c.id === conv.id ? { ...c, isArchived: newArchived } : c
            );
          }
          return updated;
        });
        // Refresh from server
        fetchConversations();
      }
    } catch (error) {
      console.error('Error toggling archive:', error);
    }
  };

  // Separate pinned conversations from grouped, filtering by archive status
  const { pinnedConversations, unpinnedGrouped, archivedCount } = useMemo(() => {
    const pinned = [];
    const unpinned = {};
    let archived = 0;

    Object.entries(grouped).forEach(([date, convs]) => {
      // Count archived conversations
      archived += convs.filter(c => c.isArchived).length;

      // Filter based on showArchived state
      const visibleConvs = convs.filter(c => showArchived ? c.isArchived : !c.isArchived);

      const pinnedInGroup = visibleConvs.filter(c => c.isPinned);
      const unpinnedInGroup = visibleConvs.filter(c => !c.isPinned);

      pinned.push(...pinnedInGroup);

      if (unpinnedInGroup.length > 0) {
        unpinned[date] = unpinnedInGroup;
      }
    });

    return { pinnedConversations: pinned, unpinnedGrouped: unpinned, archivedCount: archived };
  }, [grouped, showArchived]);

  // Get IDs of conversations that are in folders
  const folderedIds = useMemo(() => getFolderedConversationIds(), [getFolderedConversationIds, folders]);

  // Helper function to filter by project (SAT-63)
  const filterByProject = (convs) => {
    if (!selectedProject) return convs; // "All Projects" selected
    return convs.filter(conv => conv.project_id === selectedProject.id);
  };

  // Apply advanced filters (SAT-109)
  const applyAdvancedFilters = useCallback((convs) => {
    let filtered = convs;

    // Filter by selected projects (multi-select)
    if (advancedFilters.selectedProjects.length > 0) {
      filtered = filtered.filter(conv =>
        advancedFilters.selectedProjects.includes(conv.project_id)
      );
    }

    // Filter by date range
    if (advancedFilters.dateFrom) {
      const fromDate = new Date(advancedFilters.dateFrom);
      fromDate.setHours(0, 0, 0, 0);
      filtered = filtered.filter(conv => {
        const convDate = new Date(conv.updatedAt || conv.created_at || conv.time);
        return convDate >= fromDate;
      });
    }
    if (advancedFilters.dateTo) {
      const toDate = new Date(advancedFilters.dateTo);
      toDate.setHours(23, 59, 59, 999);
      filtered = filtered.filter(conv => {
        const convDate = new Date(conv.updatedAt || conv.created_at || conv.time);
        return convDate <= toDate;
      });
    }

    // Filter by selected models
    if (advancedFilters.selectedModels.length > 0) {
      filtered = filtered.filter(conv =>
        advancedFilters.selectedModels.includes(conv.model)
      );
    }

    return filtered;
  }, [advancedFilters]);

  // Check if any advanced filters are active
  const hasAdvancedFilters = advancedFilters.selectedProjects.length > 0 ||
    advancedFilters.dateFrom ||
    advancedFilters.dateTo ||
    advancedFilters.selectedModels.length > 0;

  // Use search results if available, otherwise filter locally
  const filteredGrouped = useMemo(() => {
    // If we have API search results, use them (include foldered ones in search)
    if (searchResults !== null && searchQuery.trim().length > 0) {
      // Apply project filter and advanced filters to search results
      return Object.entries(searchResults).reduce((acc, [date, convs]) => {
        let filtered = filterByProject(convs);
        filtered = applyAdvancedFilters(filtered);
        if (filtered.length > 0) {
          acc[date] = filtered;
        }
        return acc;
      }, {});
    }

    // Local filter as fallback (when no API results or typing)
    if (searchQuery || hasAdvancedFilters) {
      return Object.entries(grouped).reduce((acc, [date, convs]) => {
        let filtered = convs;
        // Apply text search filter
        if (searchQuery) {
          filtered = filtered.filter(conv =>
            conv.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
            (conv.preview && conv.preview.toLowerCase().includes(searchQuery.toLowerCase()))
          );
        }
        // Apply project filter
        filtered = filterByProject(filtered);
        // Apply advanced filters (SAT-109)
        filtered = applyAdvancedFilters(filtered);
        if (filtered.length > 0) {
          acc[date] = filtered;
        }
        return acc;
      }, {});
    }

    // Filter out conversations that are in folders (when not searching)
    const withoutFoldered = {};
    Object.entries(unpinnedGrouped).forEach(([date, convs]) => {
      let filtered = convs.filter(conv => !folderedIds.has(conv.id));
      // Apply project filter
      filtered = filterByProject(filtered);
      // Apply advanced filters (SAT-109)
      filtered = applyAdvancedFilters(filtered);
      if (filtered.length > 0) {
        withoutFoldered[date] = filtered;
      }
    });

    return withoutFoldered;
  }, [searchResults, searchQuery, grouped, unpinnedGrouped, folderedIds, selectedProject, hasAdvancedFilters, applyAdvancedFilters]);

  // Filter pinned conversations when searching or filtering by project
  const filteredPinned = useMemo(() => {
    let filtered = pinnedConversations;

    // Apply project filter
    if (selectedProject) {
      filtered = filtered.filter(conv => conv.project_id === selectedProject.id);
    }

    // Apply search filter
    if (searchQuery) {
      filtered = filtered.filter(conv =>
        conv.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (conv.preview && conv.preview.toLowerCase().includes(searchQuery.toLowerCase()))
      );
    }

    // Apply advanced filters (SAT-109)
    filtered = applyAdvancedFilters(filtered);

    return filtered;
  }, [pinnedConversations, searchQuery, selectedProject, applyAdvancedFilters]);

  return (
    <nav
      className={`
        relative flex flex-col h-full
        bg-gray-50 dark:bg-gray-900
        border-r border-gray-200 dark:border-gray-700
        transition-all duration-300 ease-in-out
        ${sidebarOpen ? '' : 'ml-[-280px] md:ml-0 md:w-0'}
      `}
      style={{
        width: sidebarOpen ? `${sidebarWidth}px` : '0px',
        minWidth: sidebarOpen ? `${sidebarWidth}px` : '0px'
      }}
      role="navigation"
      aria-label="Conversation sidebar"
      aria-hidden={!sidebarOpen}
    >
      {/* Sidebar content - hidden when collapsed.
          NOTE: no overflow-y on this wrapper. Inner regions (conversation list
          + admin panel) manage their own scroll so admin items can't push
          Favorites off-screen. */}
      <div className={`
        flex flex-col h-full overflow-x-hidden
        ${sidebarOpen ? 'opacity-100' : 'opacity-0'}
        transition-opacity duration-200
      `}>
        {/* Header with New Chat button */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
          <Link
            to="/"
            data-tour="new-chat"
            className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5
              bg-decisiv-blue text-white rounded-lg
              hover:bg-opacity-90 transition-colors font-medium"
            aria-label="Start a new chat conversation"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            New Chat
          </Link>

          {/* Collapse button */}
          <button
            onClick={toggleSidebar}
            className="ml-2 p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200
              hover:bg-gray-200 dark:hover:bg-gray-700 rounded-lg transition-colors"
            title="Collapse sidebar"
            aria-label="Collapse sidebar"
            aria-expanded={sidebarOpen}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
            </svg>
          </button>
        </div>

        {/* Project Selector (SAT-63) */}
        <div className="px-3 pb-2" data-tour="project-selector">
          <ProjectSelector
            selectedProject={selectedProject}
            onSelectProject={setSelectedProject}
            onOpenSettings={(project) => {
              // Open project settings modal (SAT-64)
              setProjectSettingsModal({ visible: true, project });
            }}
          />
        </div>

        {/* Search */}
        <div className="p-3 pt-0" data-tour="search-conversations">
          <div className="relative">
            <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <input
              type="search"
              placeholder="Search conversations..."
              value={searchQuery}
              onChange={handleSearchChange}
              aria-label="Search conversations"
              className="w-full pl-10 pr-10 py-2 text-sm
                bg-white dark:bg-gray-800
                border border-gray-200 dark:border-gray-700
                rounded-lg text-gray-900 dark:text-white
                placeholder-gray-500 dark:placeholder-gray-400
                focus:outline-hidden focus:ring-2 focus:ring-decisiv-blue/50"
            />
            {/* Loading indicator */}
            {isSearching && (
              <div className="absolute right-3 top-1/2 -translate-y-1/2">
                <svg className="animate-spin w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
              </div>
            )}
            {/* Clear button */}
            {!isSearching && searchQuery && (
              <button
                onClick={handleClearSearch}
                className="absolute right-3 top-1/2 -translate-y-1/2 p-0.5
                  text-gray-400 hover:text-gray-600 dark:hover:text-gray-300
                  rounded-full hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
                title="Clear search"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>
          {/* Advanced Search Filters (SAT-109) */}
          <div className="mt-2">
            <SearchFilters
              isExpanded={isFiltersExpanded}
              onToggleExpanded={() => setIsFiltersExpanded(!isFiltersExpanded)}
              onFiltersChange={setAdvancedFilters}
            />
          </div>
          {/* Search results count */}
          {(searchQuery || hasAdvancedFilters) && !isSearching && (
            <div className="mt-1.5 px-1 text-xs text-gray-500 dark:text-gray-400">
              {Object.values(filteredGrouped).flat().length + filteredPinned.length} result{(Object.values(filteredGrouped).flat().length + filteredPinned.length) !== 1 ? 's' : ''}
              {searchQuery && ` for "${searchQuery}"`}
              {hasAdvancedFilters && ' (filtered)'}
            </div>
          )}
        </div>

        {/* Conversation List */}
        <nav className="flex-1 overflow-y-auto px-2 py-2">
          {/* Show skeleton loaders while loading */}
          {isLoading ? (
            <ConversationListSkeleton count={4} />
          ) : (Object.keys(filteredGrouped).length === 0 && filteredPinned.length === 0 && folders.length === 0 && !showNewFolderInput) ? (
            <div className="px-3 py-4 text-center text-gray-500 dark:text-gray-400">
              {searchQuery || hasAdvancedFilters ? (
                <p className="text-sm">No conversations match your {searchQuery ? 'search' : 'filters'}</p>
              ) : apiError ? (
                <>
                  <svg className="w-8 h-8 mx-auto mb-2 text-gray-300 dark:text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <p className="text-sm mb-1">Couldn't load conversations</p>
                  <p className="text-xs text-gray-400 dark:text-gray-500 mb-3">Please try again or refresh the page</p>
                  <button
                    onClick={loadConversations}
                    className="text-xs text-decisiv-blue hover:underline"
                  >
                    Try again
                  </button>
                </>
              ) : (
                <>
                  <p className="text-sm mb-4">No conversations yet. Start a new chat!</p>
                  {/* New Folder button in empty state */}
                  <button
                    onClick={() => setShowNewFolderInput(true)}
                    className="inline-flex items-center gap-2 px-3 py-2 text-sm
                      text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300
                      hover:bg-gray-200 dark:hover:bg-gray-700 rounded-lg transition-colors"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 13h6m-3-3v6m-9 1V7a2 2 0 012-2h6l2 2h6a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2z" />
                    </svg>
                    <span>New Folder</span>
                  </button>
                </>
              )}
            </div>
          ) : (
          <>
          {/* Folders Section */}
          {!searchQuery && (folders.length > 0 || showNewFolderInput) && (
            <div className="mb-4" data-tour="folders">
              <div className="flex items-center justify-between gap-1 px-3 py-1.5">
                <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider flex items-center gap-1">
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
                  </svg>
                  Folders
                </span>
                <button
                  onClick={() => setShowNewFolderInput(true)}
                  className="p-1 text-gray-400 hover:text-decisiv-blue
                    hover:bg-gray-200 dark:hover:bg-gray-700 rounded transition-colors"
                  title="New Folder"
                >
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                </button>
              </div>

              {/* New folder input */}
              {showNewFolderInput && (
                <div className="flex items-center gap-2 px-3 py-2 mx-1 rounded-lg bg-gray-100 dark:bg-gray-800">
                  <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
                  </svg>
                  <input
                    ref={newFolderInputRef}
                    type="text"
                    value={newFolderName}
                    onChange={(e) => setNewFolderName(e.target.value)}
                    onKeyDown={handleFolderNameKeyDown}
                    onBlur={() => {
                      if (!newFolderName.trim()) {
                        setShowNewFolderInput(false);
                      }
                    }}
                    className="flex-1 text-sm bg-transparent border-b border-decisiv-blue
                      text-gray-900 dark:text-white focus:outline-hidden"
                    placeholder="Folder name"
                  />
                  <button
                    onClick={handleCreateFolder}
                    className="p-1 text-decisiv-blue hover:bg-gray-200 dark:hover:bg-gray-700 rounded-sm"
                    title="Create"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </button>
                </div>
              )}

              {/* Folder list */}
              <div className="space-y-0.5 mt-1">
                {folders.map(folder => (
                  <FolderItem
                    key={folder.id}
                    folder={folder}
                    onDragOver={handleFolderDragOver}
                    onDrop={handleFolderDrop}
                    searchQuery={searchQuery}
                  />
                ))}
              </div>
            </div>
          )}

          {/* New Folder button when no folders exist and not searching */}
          {!searchQuery && folders.length === 0 && !showNewFolderInput && (
            <button
              onClick={() => setShowNewFolderInput(true)}
              className="w-full flex items-center gap-2 px-3 py-2 mb-4 text-sm
                text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300
                hover:bg-gray-200 dark:hover:bg-gray-700 rounded-lg transition-colors"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 13h6m-3-3v6m-9 1V7a2 2 0 012-2h6l2 2h6a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2z" />
              </svg>
              <span>New Folder</span>
            </button>
          )}

          {/* Favorites Section */}
          {filteredPinned.length > 0 && (
            <div className="mb-4">
              <div className="flex items-center gap-1 px-3 py-1.5 text-xs font-semibold
                text-amber-600 dark:text-amber-400 uppercase tracking-wider">
                <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                </svg>
                <span>Favorites</span>
              </div>
              <ul className="space-y-1">
                {filteredPinned.map((conv) => {
                  const isActive = location.pathname === `/chat/${conv.id}`;
                  const isEditing = editingConvId === conv.id;

                  return (
                    <li key={conv.id}>
                      {isEditing ? (
                        /* Inline edit mode */
                        <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-gray-100 dark:bg-gray-800">
                          <svg className="w-4 h-4 shrink-0 text-amber-500" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M16 4v2l-1 .67L13.33 10H16v2H8v-2h2.67L9.33 6.67 8.33 6V4H16zM12 14l-4 8h2l2-4 2 4h2l-4-8z"/>
                          </svg>
                          <input
                            ref={editInputRef}
                            type="text"
                            value={editingTitle}
                            onChange={(e) => setEditingTitle(e.target.value)}
                            onKeyDown={handleEditKeyDown}
                            onBlur={handleSaveRename}
                            className="flex-1 text-sm font-medium bg-transparent border-b border-decisiv-blue
                              text-gray-900 dark:text-white focus:outline-hidden"
                            placeholder="Conversation title"
                          />
                        </div>
                      ) : (
                        /* Normal display mode */
                        <Link
                          to={`/chat/${conv.id}`}
                          draggable
                          onDragStart={(e) => handleConversationDragStart(e, conv)}
                          onContextMenu={(e) => handleContextMenu(e, conv)}
                          className={`
                            flex items-start gap-3 px-3 py-2 rounded-lg
                            transition-colors group cursor-grab active:cursor-grabbing
                            ${isActive
                              ? 'bg-decisiv-blue/10 text-decisiv-blue'
                              : 'text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'
                            }
                          `}
                        >
                          <svg className="w-4 h-4 shrink-0 mt-0.5 text-amber-500" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M16 4v2l-1 .67L13.33 10H16v2H8v-2h2.67L9.33 6.67 8.33 6V4H16zM12 14l-4 8h2l2-4 2 4h2l-4-8z"/>
                          </svg>
                          <div className="flex-1 min-w-0">
                            {/* Line 1: the title, with the row's full width. */}
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-medium truncate min-w-0 flex-1" title={conv.title}>
                                {searchQuery ? (
                                  <HighlightText text={conv.title} query={searchQuery} />
                                ) : (
                                  conv.title
                                )}
                              </span>
                              {/* Unread indicator (SAT-100) */}
                              <UnreadBadge count={getUnreadCount(conv.id)} />
                            </div>
                            {/* A preview of the answer, when there is one. */}
                            {conv.preview && (
                              <p className={`text-xs mt-0.5 truncate ${
                                isActive ? 'text-decisiv-blue/60' : 'text-gray-500 dark:text-gray-400'
                              }`}>
                                {searchQuery ? (
                                  <HighlightText text={conv.preview} query={searchQuery} />
                                ) : (
                                  conv.preview
                                )}
                              </p>
                            )}
                            {/* Last line: when, and the row's buttons, which show on hover or
                                keyboard focus. The row keeps its grab cursor for dragging. */}
                            <div className="flex items-center gap-2 mt-0.5">
                              <span
                                className={`text-xs truncate min-w-0 flex-1 ${
                                  isActive ? 'text-decisiv-blue/70' : 'text-gray-500 dark:text-gray-400'
                                }`}
                                title={conv.time ? (conv.fullTimestamp || `Last activity: ${conv.time}`) : undefined}
                              >
                                {conv.time}
                              </span>
                              <div className="flex items-center gap-0.5 shrink-0 opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity">
                                  <button
                                    onClick={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      handleTogglePin(conv);
                                    }}
                                    className="p-1 text-amber-500 hover:text-amber-600
                                      dark:hover:text-amber-400 hover:bg-gray-200 dark:hover:bg-gray-600 rounded"
                                    title="Remove from favorites"
                                  >
                                    <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 24 24">
                                      <path d="M16 4v2l-1 .67L13.33 10H16v2H8v-2h2.67L9.33 6.67 8.33 6V4H16zM12 14l-4 8h2l2-4 2 4h2l-4-8z"/>
                                    </svg>
                                  </button>
                                  <button
                                    onClick={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      handleToggleArchive(conv);
                                    }}
                                    className="p-1 text-gray-400 hover:text-gray-600
                                      dark:hover:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 rounded"
                                    title={conv.isArchived ? 'Unarchive' : 'Archive'}
                                  >
                                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      {conv.isArchived ? (
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
                                      ) : (
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4l3 3m0 0l3-3m-3 3V9" />
                                      )}
                                    </svg>
                                  </button>
                                  <button
                                    onClick={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      handleStartRename(conv);
                                    }}
                                    className="p-1 text-gray-400 hover:text-gray-600
                                      dark:hover:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 rounded"
                                    title="Rename"
                                  >
                                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                                    </svg>
                                  </button>
                                  <button
                                    onClick={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      handleShowDeleteModal(conv);
                                    }}
                                    className="p-1 text-gray-400 hover:text-red-500
                                      dark:hover:text-red-400 hover:bg-gray-200 dark:hover:bg-gray-600 rounded"
                                    title="Delete"
                                  >
                                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                    </svg>
                                  </button>
                              </div>
                            </div>
                          </div>
                        </Link>
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
          {/* Date-grouped conversations */}
          {Object.entries(filteredGrouped).map(([date, convs]) => {
            const isCollapsed = collapsedGroups[date];
            return (
            <div key={date} className="mb-4">
              <button
                onClick={() => toggleGroupCollapse(date)}
                className="w-full flex items-center gap-1 px-3 py-1.5 text-xs font-semibold
                  text-gray-500 dark:text-gray-400 uppercase tracking-wider
                  hover:bg-gray-200 dark:hover:bg-gray-700 rounded-md transition-colors
                  group"
              >
                <svg
                  className={`w-3 h-3 transition-transform duration-200 ${isCollapsed ? '' : 'rotate-90'}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
                <span className="flex-1 text-left">{date}</span>
                <span className="text-gray-400 dark:text-gray-500 font-normal opacity-0 group-hover:opacity-100 transition-opacity">
                  {convs.length}
                </span>
              </button>
              <ul
                className={`space-y-1 overflow-hidden transition-all duration-200 ease-in-out ${
                  isCollapsed ? 'max-h-0 opacity-0' : 'max-h-[2000px] opacity-100'
                }`}
              >
                {convs.map((conv) => {
                  const isActive = location.pathname === `/chat/${conv.id}`;
                  const isEditing = editingConvId === conv.id;

                  return (
                    <li key={conv.id}>
                      {isEditing ? (
                        /* Inline edit mode */
                        <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-gray-100 dark:bg-gray-800">
                          <svg className="w-4 h-4 shrink-0 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                          </svg>
                          <input
                            ref={editInputRef}
                            type="text"
                            value={editingTitle}
                            onChange={(e) => setEditingTitle(e.target.value)}
                            onKeyDown={handleEditKeyDown}
                            onBlur={handleSaveRename}
                            className="flex-1 text-sm font-medium bg-transparent border-b border-decisiv-blue
                              text-gray-900 dark:text-white focus:outline-hidden"
                            placeholder="Conversation title"
                          />
                        </div>
                      ) : (
                        /* Normal display mode */
                        <Link
                          to={`/chat/${conv.id}`}
                          draggable
                          onDragStart={(e) => handleConversationDragStart(e, conv)}
                          onContextMenu={(e) => handleContextMenu(e, conv)}
                          className={`
                            flex items-start gap-3 px-3 py-2 rounded-lg
                            transition-colors group cursor-grab active:cursor-grabbing
                            ${isActive
                              ? 'bg-decisiv-blue/10 text-decisiv-blue'
                              : 'text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'
                            }
                          `}
                        >
                          <svg className="w-4 h-4 shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                          </svg>
                          <div className="flex-1 min-w-0">
                            {/* Line 1: the title, with the row's full width. */}
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-medium truncate min-w-0 flex-1" title={conv.title}>
                                {searchQuery ? (
                                  <HighlightText text={conv.title} query={searchQuery} />
                                ) : (
                                  conv.title
                                )}
                              </span>
                              {/* Unread indicator (SAT-100) */}
                              <UnreadBadge count={getUnreadCount(conv.id)} />
                            </div>
                            {/* A preview of the answer, when there is one. */}
                            {conv.preview && (
                              <p className={`text-xs mt-0.5 truncate ${
                                isActive ? 'text-decisiv-blue/60' : 'text-gray-500 dark:text-gray-400'
                              }`}>
                                {searchQuery ? (
                                  <HighlightText text={conv.preview} query={searchQuery} />
                                ) : (
                                  conv.preview
                                )}
                              </p>
                            )}
                            {/* Last line: when, and the row's buttons, which show on hover or
                                keyboard focus. The row keeps its grab cursor for dragging. */}
                            <div className="flex items-center gap-2 mt-0.5">
                              <span
                                className={`text-xs truncate min-w-0 flex-1 ${
                                  isActive ? 'text-decisiv-blue/70' : 'text-gray-500 dark:text-gray-400'
                                }`}
                                title={conv.time ? (conv.fullTimestamp || `Last activity: ${conv.time}`) : undefined}
                              >
                                {conv.time}
                              </span>
                              <div className="flex items-center gap-0.5 shrink-0 opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity">
                                  <button
                                    onClick={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      handleTogglePin(conv);
                                    }}
                                    className="p-1 text-gray-400 hover:text-amber-500
                                      dark:hover:text-amber-400 hover:bg-gray-200 dark:hover:bg-gray-600 rounded"
                                    title={conv.isArchived ? 'Unarchive' : 'Pin'}
                                  >
                                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
                                    </svg>
                                  </button>
                                  <button
                                    onClick={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      handleToggleArchive(conv);
                                    }}
                                    className="p-1 text-gray-400 hover:text-gray-600
                                      dark:hover:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 rounded"
                                    title={conv.isArchived ? 'Unarchive' : 'Archive'}
                                  >
                                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      {conv.isArchived ? (
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
                                      ) : (
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4l3 3m0 0l3-3m-3 3V9" />
                                      )}
                                    </svg>
                                  </button>
                                  <button
                                    onClick={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      handleStartRename(conv);
                                    }}
                                    className="p-1 text-gray-400 hover:text-gray-600
                                      dark:hover:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 rounded"
                                    title="Rename"
                                  >
                                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                                    </svg>
                                  </button>
                                  <button
                                    onClick={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      handleShowDeleteModal(conv);
                                    }}
                                    className="p-1 text-gray-400 hover:text-red-500
                                      dark:hover:text-red-400 hover:bg-gray-200 dark:hover:bg-gray-600 rounded"
                                    title="Delete"
                                  >
                                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                    </svg>
                                  </button>
                              </div>
                            </div>
                          </div>
                        </Link>
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>
          );
          })}
          </>
          )}
        </nav>

        {/* Footer - Settings & Admin (collapsible region).
            For superadmins, the admin items below can total
            ~700px of height. Without bounding the footer, it pushed Favorites
            off-screen. We now collapse this region by default; user expands
            via the chevron and can drag the divider to resize. */}
        {adminPanelExpanded && (
          <HorizontalResizeHandle
            onResize={setAdminPanelHeight}
            currentHeight={adminPanelHeight}
            minHeight={120}
            maxHeight={600}
          />
        )}
        <div
          className="shrink-0 border-t border-gray-200 dark:border-gray-700 flex flex-col"
          style={{ height: adminPanelExpanded ? `${adminPanelHeight}px` : '36px' }}
        >
          {/* Chevron header — always visible */}
          <button
            onClick={toggleAdminPanel}
            className="shrink-0 w-full flex items-center justify-between px-3 py-2 text-xs font-semibold
              text-gray-500 dark:text-gray-400 uppercase tracking-wider
              hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
            title={adminPanelExpanded ? 'Collapse settings & admin' : 'Expand settings & admin'}
            aria-expanded={adminPanelExpanded}
            aria-controls="sidebar-admin-panel"
          >
            <span>Settings &amp; Admin</span>
            <svg className={`w-3.5 h-3.5 transition-transform ${adminPanelExpanded ? 'rotate-180' : ''}`}
              fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>

          {/* Expanded body — own scroll, only renders when expanded */}
          <div
            id="sidebar-admin-panel"
            className={`flex-1 min-h-0 overflow-y-auto p-3 space-y-2 ${adminPanelExpanded ? '' : 'hidden'}`}
          >
          {/* Theme Toggle */}
          <button
            onClick={toggleTheme}
            className="w-full flex items-center gap-3 px-3 py-2 text-sm
              text-gray-700 dark:text-gray-300
              hover:bg-gray-200 dark:hover:bg-gray-700 rounded-lg transition-colors"
          >
            {isDark ? (
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
              </svg>
            ) : (
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
              </svg>
            )}
            <span>{isDark ? 'Light Mode' : 'Dark Mode'}</span>
          </button>

          {/* Archived Toggle */}
          <button
            onClick={() => setShowArchived(!showArchived)}
            className={`w-full flex items-center gap-3 px-3 py-2 text-sm rounded-lg transition-colors
              ${showArchived
                ? 'bg-gray-200 dark:bg-gray-700 text-gray-900 dark:text-white'
                : 'text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'
              }`}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
            </svg>
            <span className="flex-1 text-left">
              {showArchived ? 'Show Active' : 'Show Archived'}
            </span>
            {archivedCount > 0 && (
              <span className={`text-xs px-1.5 py-0.5 rounded-full ${
                showArchived
                  ? 'bg-gray-300 dark:bg-gray-600 text-gray-700 dark:text-gray-300'
                  : 'bg-gray-200 dark:bg-gray-700 text-gray-500 dark:text-gray-400'
              }`}>
                {archivedCount}
              </span>
            )}
          </button>

          {/* Prompt Library Button (SAT-79) */}
          <button
            onClick={() => setShowPromptLibrary(true)}
            className="w-full flex items-center gap-3 px-3 py-2 text-sm rounded-lg transition-colors
              text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
            </svg>
            <span>Prompt Library</span>
          </button>

          {/* Skill catalog (skills plan step 4a) — the org's skills, click to start a chat */}
          <button
            onClick={() => navigate('/catalog')}
            className="w-full flex items-center gap-3 px-3 py-2 text-sm rounded-lg transition-colors
              text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
            </svg>
            <span>Skills</span>
          </button>

          {/* Settings Button (SAT-72) */}
          <button
            onClick={() => setShowSettingsModal(true)}
            data-tour="settings"
            className="w-full flex items-center gap-3 px-3 py-2 text-sm rounded-lg transition-colors
              text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            <span>Settings</span>
          </button>

          {/* Org Picker (DOS-27) - shown when user is in multiple orgs */}
          <OrgPicker />

          {/* Location Switcher (DOS-70) - shown when user has assigned locations */}
          <LocationSwitcher />

          {/*
            DOS-388 Phase E — single "Admin" entry replacing the 9 per-item
            links. The /admin landing page filters sub-nav + card grid by
            the user's capabilities, so each user still sees only the
            surfaces they have access to.
          */}
          {canSeeAdmin && (
            <a
              href="/admin"
              data-testid="sidebar-admin-link"
              className="w-full flex items-center gap-3 px-3 py-2 text-sm rounded-lg transition-colors
                text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              <span>Admin</span>
            </a>
          )}

          {/* User Profile */}
          {isAuthenticated && user && (
            <div className="flex items-center gap-2 px-3 py-2">
              <div className="w-8 h-8 bg-decisiv-blue rounded-full flex items-center justify-center shrink-0">
                <span className="text-white text-sm font-bold">
                  {user.name?.charAt(0).toUpperCase() || 'U'}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                  {user.name || 'User'}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                  {org?.name || user.email}
                </p>
                {role && (
                  <span className="inline-block mt-0.5 text-xs px-1.5 py-0.5 rounded bg-gray-100 dark:bg-gray-700
                    text-gray-600 dark:text-gray-300 capitalize">
                    {role.replace('_', ' ')}
                  </span>
                )}
              </div>
              <button
                onClick={logout}
                title="Sign out"
                className="shrink-0 p-1.5 rounded-md text-gray-400 hover:text-gray-600
                  dark:hover:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                </svg>
              </button>
            </div>
          )}
          </div>{/* end #sidebar-admin-panel inner scroll */}
        </div>{/* end collapsible footer wrapper */}
        <BuildInfo />
      </div>

      {/* Resize Handle */}
      {sidebarOpen && (
        <ResizeHandle
          direction="right"
          onResize={setSidebarWidth}
          minWidth={220}
          maxWidth={400}
        />
      )}

      {/* Context Menu */}
      {contextMenu.visible && (
        <div
          className="fixed z-50 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700
            rounded-lg shadow-lg py-1 min-w-[150px]"
          style={{ top: contextMenu.y, left: contextMenu.x }}
          onClick={(e) => e.stopPropagation()}
        >
          <button
            onClick={() => handleTogglePin(contextMenu.conv)}
            className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 dark:text-gray-300
              hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
          >
            <svg className="w-4 h-4" fill={contextMenu.conv?.isPinned ? 'currentColor' : 'none'} stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={contextMenu.conv?.isPinned ? 0 : 2}
                d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
            </svg>
            {contextMenu.conv?.isPinned ? 'Remove from favorites' : 'Add to favorites'}
          </button>
          <button
            onClick={() => handleToggleArchive(contextMenu.conv)}
            className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 dark:text-gray-300
              hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              {contextMenu.conv?.isArchived ? (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
              ) : (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4l3 3m0 0l3-3m-3 3V9" />
              )}
            </svg>
            {contextMenu.conv?.isArchived ? 'Unarchive' : 'Archive'}
          </button>
          <button
            onClick={() => handleStartRename(contextMenu.conv)}
            className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 dark:text-gray-300
              hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
            </svg>
            Rename
          </button>
          <button
            onClick={() => handleDuplicate(contextMenu.conv)}
            className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 dark:text-gray-300
              hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
            </svg>
            Duplicate
          </button>
          {/* Move to Project button (SAT-65) */}
          <button
            onClick={() => handleShowMoveModal(contextMenu.conv)}
            className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 dark:text-gray-300
              hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
            </svg>
            Move to Project
          </button>
          <button
            onClick={() => handleShowDeleteModal(contextMenu.conv)}
            className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-600 dark:text-red-400
              hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
            Delete
          </button>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        isOpen={deleteModal.visible}
        onClose={handleCloseDeleteModal}
        onConfirm={handleConfirmDelete}
        title="Delete Conversation"
        message={`Are you sure you want to delete "${deleteModal.conv?.title || 'this conversation'}"? This action can be undone briefly.`}
        confirmText="Delete"
        cancelText="Cancel"
        variant="danger"
        loading={isDeleting}
      />

      {/* Undo Toast */}
      <Toast
        isVisible={undoToast.visible}
        message="Conversation deleted"
        actionLabel="Undo"
        onAction={handleUndoDelete}
        onClose={handleCloseUndoToast}
        duration={5000}
      />

      {/* Settings Modal (SAT-72) */}
      <SettingsModal
        isOpen={showSettingsModal}
        onClose={() => setShowSettingsModal(false)}
      />

      {/* Project Settings Modal (SAT-64) */}
      <ProjectSettingsModal
        project={projectSettingsModal.project}
        isOpen={projectSettingsModal.visible}
        onClose={() => setProjectSettingsModal({ visible: false, project: null })}
        onSave={(updatedProject) => {
          // Update project in context (SAT-64)
          updateProject(updatedProject);
        }}
      />

      {/* Move to Project Modal (SAT-65) */}
      <MoveToProjectModal
        isOpen={moveModal.visible}
        onClose={() => setMoveModal({ visible: false, conv: null })}
        conversation={moveModal.conv}
        onMove={handleMoveToProject}
      />

      {/* Prompt Library Modal (SAT-79) — contained=true keeps backdrop within sidebar so resize handle stays accessible */}
      <PromptLibraryModal
        isOpen={showPromptLibrary}
        onClose={() => setShowPromptLibrary(false)}
        contained
        onInsertPrompt={(promptText) => {
          // Dispatch custom event for chat input to receive
          window.dispatchEvent(new CustomEvent('insertPrompt', { detail: { prompt: promptText } }));
          setShowPromptLibrary(false);
        }}
      />
    </nav>
  );
}

export default Sidebar;
